import React, { useState, useEffect } from 'react'
import noop from 'lodash/noop'
import { TextInputOld, Button, Dropdown, IDropdownItem } from '@doseme/cohesive-ui'
import { isLoaded, isLoading } from 'flux-entities'
import moment from 'moment'

import { TSSORealm } from '../../../../types/ssoRealm'
import { TProps } from './types'
import { SSO_REALMS_URL } from '../../../../constants/urls/frontend'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'
import { ILabelWithValue } from '../../../../components/SubscriptionsInputModal/types'

type TKey = 'idpHint' | 'realm' | 'installation'

const Edit: React.FC<TProps> = (props) => {
  const { ssoRealms, fetchSsoRealms, updateSsoRealm, installations, fetchInstallations, history, match } = props

  const [copy, updateCopy] = useState<TSSORealm>({
    id: 0,
    realm: '',
    idpHint: '',
    installation: 0,
    created: moment(),
    updated: moment()
  })
  const [error, updateError] = useState('')

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    idpHint: {
      touched: true,
      valid: true
    },
    realm: {
      touched: true,
      valid: true
    },
    installation: {
      touched: true,
      valid: true
    }
  })

  useEffect(() => {
    if (!ssoRealms.touched) {
      fetchSsoRealms()
    }
  }, [ssoRealms.touched, fetchSsoRealms])

  useEffect(() => {
    if (!installations.touched) {
      fetchInstallations()
    }
  }, [installations.touched, fetchInstallations])

  useEffect(() => {
    const loadedSsoRealm = ssoRealms.all[match.params.id]
    if (loadedSsoRealm) {
      updateCopy(loadedSsoRealm)
    }
  }, [ssoRealms.all, updateCopy, match.params.id])

  const ssoRealm = ssoRealms.all[match.params.id]

  if (!ssoRealm && isLoaded(ssoRealms)) {
    return <div>No SSO Realm was found with the id {match.params.id}</div>
  }

  if (copy.id === 0 || !isLoaded(installations)) {
    return <div>Loading...</div>
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (valid) {
      updateCopy({ ...copy, [name]: value })
    }

    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    updateError('')

    const { status, message } = await updateSsoRealm({ ...copy, updated: moment() })
    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(SSO_REALMS_URL)
  }

  const handleInstallationSelect = (item: IDropdownItem) => {
    const numericId = parseInt(item.value, 10)
    updateCopy({ ...copy, installation: numericId })
  }

  const installationList = installations.ids.reduce<ILabelWithValue[]>((acc, curr) => {
    const installation = installations.all[curr]
    return [...acc, { label: installation.code, value: installation.id.toString() }]
  }, [])

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    (field) => fieldsValidity[field].valid && fieldsValidity[field].touched
  )

  return (
    <FormPageLayout
      title='Edit SSO Realm'
      testid='edit-sso-realm'
      subtitle={`${ssoRealm.realm} - ${installations.all[ssoRealm.installation].code}`}
      handleBackClick={() => history.push(SSO_REALMS_URL)}
    >
      <StyledForm handleSubmit={handleSubmit}>
        <TextInputOld
          label='Realm'
          name='realm'
          type='text'
          data-test='realm'
          defaultValue={copy.realm}
          onChange={noop}
          readOnly
          disabled
        />
        <br />

        <TextInputOld
          label='IDP Hint'
          name='idpHint'
          data-test='idp-hint'
          type='text'
          defaultValue={copy.idpHint}
          onBlur={(value, valid) => handleInput('idpHint', value, valid)}
        />
        <br />

        <Dropdown
          fieldState='valid'
          placeholder={installations.all[copy.installation].code}
          showSearchThreshold={0}
          data={installationList}
          onSelect={handleInstallationSelect}
        />
        <br />
        <hr />

        <div className='d-flex w-100 justify-content-end'>
          <Button type='submit' variant='primary' onClick={noop} disabled={!formValid} loading={isLoading(ssoRealms)}>
            Update
          </Button>
        </div>
      </StyledForm>

      {error}
    </FormPageLayout>
  )
}

export { Edit }
